// src/components/quote/RoyaltyPanel.jsx
//
// The /quote page's "Retail pricing & royalties" section, built from the owner's prototype
// (book-pricing-estimator-5): what readers pay, how each sale is shared and for how long,
// and whether the print run pays for itself.
//
// Controlled: QuotePage owns `plan`, because "Start your project" saves it onto the new
// manuscript (as `sales_plan`). All the maths lives in utils/royalty.js; the one print
// figure it needs, a copy's cost after the volume discount, is the server's
// `net_price_per_unit` from the quote.
//
// Two departures from the prototype, both decided on the ticket:
//   - ReVisionari's row is locked to the publishing tiers on /pricing (15% or 35%, 24
//     months); the prototype let the author type any percentage there.
//   - Over 100%, every percentage and the total turn red (the error token). Under 100% is
//     unfinished rather than wrong, so it gets a quiet "not assigned yet" note instead.
//     Neither blocks "Start your project".
//
// Styling is in styles/Quote.css, scoped under .quote-page, like the rest of the page.

import { useEffect, useRef, useState } from 'react';
import { tokens } from '../../theme';
import ProfitGauge from './ProfitGauge';
import RoyaltyFlow from './RoyaltyFlow';
import {
  FORMAT_NAMES,
  MAX_COLLABORATORS,
  PLATFORM_TIERS,
  ROY_ROLES,
  ROY_TERMS,
  addCollaborator,
  bundleNote,
  collaboratorCount,
  flowWarnings,
  money,
  projectPlan,
  removeParty,
  signedMoney,
  splitStatus,
  updateParty,
  withPlatformTier,
} from '../../utils/royalty';

const FLOW_TABS = ['paperback', 'ebook', 'bundle'];

export default function RoyaltyPanel({ plan, onChange, quote }) {
  const [flowFormat, setFlowFormat] = useState('paperback');
  const [pctDraft, setPctDraft] = useState({}); // in-progress text for the share inputs
  const focusParty = useRef(null);

  const qty = quote?.quantity ?? 0;
  const proj = projectPlan(plan, {
    unitCost: quote?.net_price_per_unit ?? 0,
    qty,
    total: quote?.total ?? 0,
  });
  const { on } = proj;
  const status = splitStatus(plan.parties);
  const over = status.state === 'over';

  // A tab whose format was just switched off falls back to Paperback.
  const tabFormatOn = on[flowFormat];
  useEffect(() => {
    if (!tabFormatOn) setFlowFormat('paperback');
  }, [tabFormatOn]);

  // Focus the name field of a collaborator that was just added.
  useEffect(() => {
    if (!focusParty.current) return;
    document.getElementById(`sp-name-${focusParty.current}`)?.focus();
    focusParty.current = null;
  });

  const setPrice = (format, value) => onChange({ ...plan, prices: { ...plan.prices, [format]: value } });
  const setSold = (format, value) => onChange({ ...plan, sold: { ...plan.sold, [format]: value } });

  const setPct = (id, text) => {
    setPctDraft((prev) => ({ ...prev, [id]: text }));
    const v = parseFloat(text);
    onChange(updateParty(plan, id, { pct: Number.isNaN(v) ? 0 : Math.max(0, Math.min(100, v)) }));
  };
  const endPct = (id) => setPctDraft((prev) => {
    const next = { ...prev };
    delete next[id];
    return next;
  });

  const handleAdd = () => {
    const next = addCollaborator(plan);
    if (next === plan) return;
    focusParty.current = next.parties[next.parties.length - 1].id;
    onChange(next);
  };

  const full = collaboratorCount(plan) >= MAX_COLLABORATORS;
  const active = on[flowFormat] ? flowFormat : 'paperback';
  const breakdown = proj.breakdowns[active];
  const warnings = flowWarnings(breakdown);

  const gaugeLines = [`Print run ${money(quote?.total ?? 0)} · your sales ${money(proj.salesTotal)}`];
  if (proj.profit < 0 && proj.breakEven !== null) {
    gaugeLines.push(
      Number.isFinite(proj.breakEven)
        ? `Break even at ~${proj.breakEven.toLocaleString('en-US')} paperbacks`
        : "Can't break even on this run at this price"
    );
  }

  const soldNote = proj.overPrinted
    ? `Over the ${qty.toLocaleString('en-US')} printed`
    : `Estimate · of ${qty.toLocaleString('en-US')} printed`;

  const pctInput = (p, readOnly) => (
    <div className="sp-pct">
      <input
        type="number"
        className={`sp-in-pct${over ? ' over' : ''}${readOnly ? ' locked' : ''}`}
        min="0"
        max="100"
        step="0.5"
        value={readOnly ? p.pct : (pctDraft[p.id] ?? p.pct)}
        readOnly={readOnly}
        title={readOnly ? 'Set by the publishing tier' : undefined}
        onChange={readOnly ? undefined : (e) => setPct(p.id, e.target.value)}
        onBlur={readOnly ? undefined : () => endPct(p.id)}
        aria-label={`${p.id === 'platform' ? 'ReVisionari' : p.name || 'Collaborator'} share percent`}
        aria-invalid={over || undefined}
      />
    </div>
  );

  const dot = (p) => <span className="sp-dot" style={{ background: tokens.chart[p.series] }} />;

  const renderRow = (p) => {
    if (p.id === 'platform') {
      return (
        <div className="sp-row sp-platform" key={p.id}>
          <div className="sp-who">
            {dot(p)}
            <div className="sp-edit">
              <div className="sp-name">{p.name}</div>
              <select
                value={plan.platformTier}
                onChange={(e) => {
                  // The tier rewrites the author's share too; drop any half-typed share
                  // so the field shows the new value rather than what was being typed.
                  setPctDraft({});
                  onChange(withPlatformTier(plan, e.target.value));
                }}
                aria-label="Publishing tier"
              >
                {PLATFORM_TIERS.map((t) => (
                  <option key={t.id} value={t.id}>{`${t.name} · ${t.pct}%`}</option>
                ))}
              </select>
            </div>
          </div>
          {pctInput(p, true)}
          <div className="sp-term"><span className="sp-fixed">24 months · set by tier</span></div>
          <div />
        </div>
      );
    }
    if (p.id === 'author') {
      return (
        <div className="sp-row" key={p.id}>
          <div className="sp-who">
            {dot(p)}
            <div style={{ minWidth: 0 }}>
              <div className="sp-name">{p.name}</div>
              <div className="sp-role">{p.role}</div>
            </div>
          </div>
          {pctInput(p, false)}
          <div className="sp-term"><span className="sp-fixed">Perpetual · rights holder</span></div>
          <div />
        </div>
      );
    }
    return (
      <div className="sp-row" key={p.id}>
        <div className="sp-who">
          {dot(p)}
          <div className="sp-edit">
            <input
              type="text"
              id={`sp-name-${p.id}`}
              value={p.name}
              placeholder="Name"
              maxLength={80}
              onChange={(e) => onChange(updateParty(plan, p.id, { name: e.target.value }))}
              aria-label="Name"
            />
            <select
              value={p.role}
              onChange={(e) => onChange(updateParty(plan, p.id, { role: e.target.value }))}
              aria-label="Role"
            >
              {ROY_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        {pctInput(p, false)}
        <div className="sp-term">
          <select
            value={p.term}
            onChange={(e) => onChange(updateParty(plan, p.id, { term: e.target.value }))}
            aria-label="Term"
          >
            {ROY_TERMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <button
            type="button"
            className="sp-remove"
            onClick={() => onChange(removeParty(plan, p.id))}
            aria-label={`Remove ${p.name || 'collaborator'}`}
          >
            &times;
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="roy-panel reveal">
      <div className="panel-head">
        <div className="panel-badge" aria-hidden="true">💰</div>
        <div className="panel-title">Retail pricing &amp; royalties</div>
      </div>
      <p className="panel-sub">
        Set what readers pay and how earnings are shared. Print cost per copy comes from your quote above.
      </p>

      <div className="roy-top">
        <div className="rt-inputs">
          <div className="rt-field">
            <label htmlFor="rtPaperback">Paperback price</label>
            <div className="rt-money">
              <input type="number" id="rtPaperback" min="0" step="0.01" value={plan.prices.paperback}
                onChange={(e) => setPrice('paperback', e.target.value)} />
            </div>
          </div>
          <div className="rt-field">
            <label htmlFor="rtEbook">eBook price</label>
            <div className="rt-money">
              <input type="number" id="rtEbook" min="0" step="0.01" value={plan.prices.ebook}
                onChange={(e) => setPrice('ebook', e.target.value)} />
            </div>
            <div className="rt-note">Blank = no eBook</div>
          </div>
          <div className="rt-field">
            <label className="rt-check">
              <input type="checkbox" checked={plan.bundleOn}
                onChange={(e) => onChange({ ...plan, bundleOn: e.target.checked })} />
              Bundle price
            </label>
            <div className="rt-money">
              <input type="number" min="0" step="0.01" value={plan.prices.bundle} disabled={!on.bundle}
                onChange={(e) => setPrice('bundle', e.target.value)} aria-label="Bundle price" />
            </div>
            <div className="rt-note">{bundleNote(plan)}</div>
          </div>
          <div className="rt-field">
            <label htmlFor="rtSoldPb">Paperbacks sold</label>
            <input type="number" id="rtSoldPb" min="0" step="1" value={plan.sold.paperback}
              onChange={(e) => setSold('paperback', e.target.value)} />
            <div className="rt-note">{soldNote}</div>
          </div>
          <div className="rt-field">
            <label htmlFor="rtSoldEb">eBooks sold</label>
            <input type="number" id="rtSoldEb" min="0" step="1" value={plan.sold.ebook} disabled={!on.ebook}
              onChange={(e) => setSold('ebook', e.target.value)} />
            <div className="rt-note">Estimate</div>
          </div>
          {on.bundle && (
            <div className="rt-field">
              <label htmlFor="rtSoldBundle">Bundles sold</label>
              <input type="number" id="rtSoldBundle" min="0" step="1" value={plan.sold.bundle}
                onChange={(e) => setSold('bundle', e.target.value)} />
            </div>
          )}
        </div>

        <ProfitGauge profit={proj.profit} ratio={proj.ratio} lines={gaugeLines} />
      </div>

      <div className="split-head">
        <div className="split-title">Royalty split &amp; term</div>
        <div className={`split-total ${status.state}`} role="status">{status.label}</div>
      </div>
      <p className="panel-sub split-sub">
        Shares are taken from what&apos;s left after the print cost (eBooks have no print cost).
      </p>
      <div className="split-list">
        <div className="sp-row sp-head">
          <span>Party</span><span>Share</span><span className="sp-term">Term</span><span />
        </div>
        {plan.parties.map(renderRow)}
      </div>
      <button type="button" className="sp-add" onClick={handleAdd} disabled={full}>
        {full ? `Up to ${MAX_COLLABORATORS} collaborators` : '+ Add co-author or collaborator'}
      </button>

      <div className="flow-head">
        <div className="split-title">Where each sale goes</div>
        <div className="flow-tabs" role="tablist" aria-label="Format">
          {FLOW_TABS.filter((f) => on[f]).map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              className={`flow-tab${f === active ? ' active' : ''}`}
              aria-selected={f === active}
              onClick={() => setFlowFormat(f)}
            >
              {FORMAT_NAMES[f]}
            </button>
          ))}
        </div>
      </div>
      <div className="flow-card">
        <div className="fc-title">Royalty Split &amp; Term Flow Diagram</div>
        <RoyaltyFlow breakdown={breakdown} />
        {warnings.length > 0 && (
          <div className="flow-warn">
            {warnings.map((w) => <div key={w}>{w}</div>)}
          </div>
        )}
      </div>

      <div className="net-cards">
        <div className="net-card">
          <div>
            <div className="nc-title">Your net per paperback</div>
            <div className="nc-sub">Your share after print cost</div>
          </div>
          <div className={`nc-val${proj.perSale.paperback < 0 ? ' neg' : ''}`}>{signedMoney(proj.perSale.paperback)}</div>
        </div>
        {on.ebook && (
          <div className="net-card">
            <div>
              <div className="nc-title">Your net per eBook</div>
              <div className="nc-sub">Digital delivery · no print cost</div>
            </div>
            <div className="nc-val">{signedMoney(proj.perSale.ebook)}</div>
          </div>
        )}
        {on.bundle && (
          <div className="net-card">
            <div>
              <div className="nc-title">Your net per bundle</div>
              <div className="nc-sub">One printed copy included</div>
            </div>
            <div className={`nc-val${proj.perSale.bundle < 0 ? ' neg' : ''}`}>{signedMoney(proj.perSale.bundle)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
